import type { AriaAttributes, Ref } from 'react';
import * as React from 'react';
import { useCallback } from 'react';
import type { ValidIconName } from './Icon';
import { useSingleAndDoubleClick } from '../../lib';
import { useLayerDeferedVisibility } from '../../lib/hooks/useLayer';
import type { UseHoverOptions, UseLayerOptions } from 'react-laag';
import { useHover } from 'react-laag';
import type { ClassNameProp } from '..';
import { ButtonIcon } from './internal/ButtonIcon';
import {
  DEFAULT_HOVER_CONFIG,
  DEFAULT_PLACEMENT_CONFIG,
  mergeRefs
} from '../../util';

/**
 * Properties used for a {@link Tool}.
 */
export interface ToolProps
  extends ClassNameProp,
    Pick<AriaAttributes, 'aria-label' | 'aria-expanded'> {
  tooltipOptions?: Partial<UseHoverOptions> & Partial<UseLayerOptions>;
  /**
   * Defines the look of the tool.
   */
  variant?: 'transparent' | 'gray' | 'white';
  /**
   * The tool icon.
   */
  icon: ValidIconName;
  /**
   * Tool click handler.
   */
  onClick: React.MouseEventHandler;
  /**
   * Optional tool text.
   */
  text?: string;
  /**
   * If the tool is disabled.
   */
  disabled?: boolean;
  /**
   * Tool tooltip.
   */
  tooltip?: string;
  /**
   * Multiplier applied to the icon's own font-size when computing its width
   * on mobile app WebViews, to work around Android's font-boost not affecting em-based sizing.
   */
  fontBoostWidthMultiplier?: number;
  /**
   * Multiplier applied to the icon's own font-size when computing its height
   * on mobile app WebViews, to work around Android's font-boost not affecting em-based sizing.
   */
  fontBoostHeightMultiplier?: number;
}

/**
 * A simple tool.
 *
 * @param properties properties for the tool. At least one of {text, tooltip} attributes is required
 * @constructor
 * @return the created {@link React.JSX.Element} for the tool
 */
export function Tool({
  variant,
  onClick,
  tooltip,
  tooltipOptions,
  className,
  disabled,
  text,
  icon,
  fontBoostWidthMultiplier,
  fontBoostHeightMultiplier,
  'aria-expanded': ariaExpanded,
  'aria-label': ariaLabel,
  ref,
  ...props
}: Readonly<
  ToolProps &
    (Required<Pick<ToolProps, 'tooltip'>> | Required<Pick<ToolProps, 'text'>>)
> & {
  ref?: Ref<HTMLButtonElement>;
}): React.JSX.Element {
  const [isOpen, hoverProps] = useHover({
    delayEnter: tooltipOptions?.delayEnter ?? DEFAULT_HOVER_CONFIG.delayEnter,
    delayLeave: tooltipOptions?.delayLeave ?? DEFAULT_HOVER_CONFIG.delayLeave,
    hideOnScroll: tooltipOptions?.hideOnScroll
  });
  const { renderLayer, triggerProps, layerProps } = useLayerDeferedVisibility({
    isOpen,
    ...DEFAULT_PLACEMENT_CONFIG
  });

  // create click handlers
  // click handler which automatically hides the tooltip
  const onClickHidingTooltip = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      hoverProps.onMouseLeave();
      onClick(e);
    },
    [onClick, hoverProps]
  );
  // passing no dblClickHandler will return a handler that simply stops propagation
  const [clickHandler, dblClickHandler] = useSingleAndDoubleClick(
    onClickHidingTooltip
  );

  const classes = ['ehs-tool'];
  if (!variant) {
    variant = 'transparent';
  }
  classes.push(variant);
  if (disabled) classes.push('disabled');
  if (className) classes.push(className);
  return (
    <>
      {isOpen &&
        renderLayer(
          <div {...layerProps} className="ehs-tooltip">
            {tooltip}
          </div>
        )}
      <button
        {...props}
        {...triggerProps}
        ref={mergeRefs([triggerProps.ref, ref])}
        {...(tooltip ? hoverProps : {})}
        className={classes.join(' ')}
        onClick={disabled ? undefined : clickHandler}
        onDoubleClick={dblClickHandler}
        aria-label={ariaLabel ?? (text || tooltip || undefined)}
        type="button"
        aria-expanded={ariaExpanded}
        disabled={disabled}
        aria-disabled={disabled ? true : undefined}
      >
        <ButtonIcon
          iconName={icon}
          fontBoostWidthMultiplier={fontBoostWidthMultiplier}
          fontBoostHeightMultiplier={fontBoostHeightMultiplier}
        />
        {text ? <span className={'tool-text'}>{text}</span> : <></>}
      </button>
    </>
  );
}
